











































































































































import { FormBlock, FormCard } from '@/app/components';
import FileDropTarget from './FileDropTarget.vue';
import FileFormatSelector from './FileFormatSelector.vue';
import { useFilters } from '@/app/composable';
import { DocumentIcon } from '@vue-hero-icons/outline';
import { ExclamationIcon } from '@vue-hero-icons/solid';
import { computed, defineComponent, PropType, ref } from '@vue/composition-api';
import * as R from 'ramda';
import { ValidationProvider } from 'vee-validate';
import { useHarvester } from '../../../composable';
import { SAMPLE_LIMIT } from '../../../constants';
import { TFile } from '../../../types';

export default defineComponent({
    name: 'SampleUpload',
    components: {
        FileDropTarget,
        FileFormatSelector,
        FormBlock,
        FormCard,
        ValidationProvider,
        DocumentIcon,
        ExclamationIcon,
    },
    props: {
        sampleTypes: {
            type: Array as PropType<string[]>,
            default: () => [],
        },
        configuration: {
            type: Object,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        processedSample: {
            type: Array,
            required: false,
        },
        sampleFile: {
            type: File as PropType<TFile | null>,
            required: false,
        },
        sample: {
            type: [Array, Object],
            required: false,
        },
        canUploadMore: {
            type: Boolean,
            default: false,
        },
        isOnPremise: {
            type: Boolean,
            default: false,
        },
        showFormatSelections: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        showSampleFileUpload: {
            type: Boolean,
            default: true,
        },
        sampleRequired: {
            type: Boolean,
            default: true,
        },
        formatTitle: {
            type: String,
            default: 'Format',
        },
        sampleTitle: {
            type: String,
            default: 'Sample Upload',
        },
        bare: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { root, emit }) {
        const { formatBytes } = useFilters();
        const sampleCroppedMessage = computed(() => (props.configuration.isSampleCropped ? 'cropped, ' : ''));
        const fileTypeRef = computed(() => props.configuration.fileType);
        const sampleUploadRef = ref<any>(null);

        const {
            parseJSON,
            checkInvalidXML,
            parseXML,
            acceptedFiles,
            parseCSV,
            invalidFormat,
            emptyFile,
            valPercentage,
            noCSVData,
            duplicateHeaders,
            checkInvalidCSV,
            checkInvalidParquet,
            showValidationBar,
        } = useHarvester(root, emit, fileTypeRef);

        const getSampleCSV = async (file: any) => {
            if (!file) return;
            const results = await parseCSV(file, false, 50);
            emit('set-configuration-params', R.pick(['delimiter', 'linebreak', 'fields'], results.meta));
            emit('sample-uploaded', results.data);
        };

        const sampleUploaded = async (fileOrEvent: any) => {
            const file = fileOrEvent instanceof File ? fileOrEvent : fileOrEvent.target.files[0];
            if (file.size > SAMPLE_LIMIT) {
                (root as any).$toastr.e(`The sample file should not exceed 500KB.`, 'Sample file too large!');
                emit('files-changed', { sampleFile: null });
                emit('clear-error-alert');
                await sampleUploadRef.value.validate();
                return;
            }
            if (R.has('uploadQueryId', props.configuration.params))
                emit('set-configuration-params', R.pick(['uploadQueryId'], props.configuration.params));
            emit('sample-cropped', false);
            emit('clear-files');

            switch (props.configuration.fileType) {
                case 'csv':
                    emit('files-changed', { sampleFile: null });
                    await checkInvalidCSV(file);
                    if (invalidFormat.value || duplicateHeaders.value || emptyFile.value) {
                        const message = emptyFile.value
                            ? 'Empty CSV file!'
                            : invalidFormat.value
                            ? 'Invalid CSV format!'
                            : 'Duplicate columns have been detected in the file!';
                        (root as any).$toastr.e(message, 'Error');
                        emit(
                            'set-error-alert',
                            duplicateHeaders.value
                                ? `Duplicate columns have been detected in the Sample file "${file.name}"`
                                : null,
                        );
                    } else if (noCSVData.value) {
                        (root as any).$toastr.e('The CSV file contains no data!', 'No Data!');
                    } else {
                        emit('files-changed', { sampleFile: file });
                        await getSampleCSV(file);
                        emit('clear-error-alert');
                        emit('clear-actual-file');
                    }
                    break;
                case 'json':
                    emit('files-changed', { sampleFile: file });
                    await parseJSON(file);
                    emit('clear-actual-file');
                    break;
                case 'xml':
                    await checkInvalidXML(file);
                    if (emptyFile.value) {
                        emit('files-changed', { sampleFile: null });
                        (root as any).$toastr.e('Empty XML file!', 'Error');
                    } else if (invalidFormat.value) {
                        emit('files-changed', { sampleFile: null });
                        (root as any).$toastr.e('Invalid XML format!', 'Error');
                    } else {
                        emit('files-changed', { sampleFile: file });
                        await parseXML(file);
                        emit('clear-actual-file');
                    }
                    break;
                case 'parquet':
                    await checkInvalidParquet(file);
                    if (emptyFile.value) {
                        emit('files-changed', { sampleFile: null });
                        (root as any).$toastr.e('Empty parquet file!', 'Error');
                    } else {
                        emit('files-changed', { sampleFile: file });
                        await getSampleParquet(file);
                        emit('clear-actual-file');
                    }
                    break;

                default:
                // Do nothing
            }
            await sampleUploadRef.value.validate();
        };

        const getSampleParquet = async (file: any) => {
            if (!file) return;
            emit('sample-uploaded', props.processedSample);
        };

        const clearFiles = () => {
            emit('files-changed', { sampleFile: null, dataFile: null });
            emit('set-configuration-params', {});
            emit('clear-error-alert');
            emit('format-changed');
            noCSVData.value = false;
        };

        const sampleMayBeCroppedMessage = computed(() => {
            if (props.configuration.fileType && props.configuration.fileType !== 'csv') {
                return 'Sample may be cropped if required. Ensure that a small sample contains all necessary fields.';
            }
            return null;
        });

        const formatHint = computed(() => {
            if (!props.configuration.fileType) return null;
            const fileType = props.configuration.fileType;
            return `${fileType === 'parquet' ? 'Parquet' : fileType.toUpperCase()} format expected`;
        });

        const emptySampleData = computed(() => R.isNil(props.sample) || R.isEmpty(props.sample));

        return {
            emptySampleData,
            formatHint,
            sampleUploadRef,
            formatBytes,
            showValidationBar,
            valPercentage,
            noCSVData,
            sampleUploaded,
            clearFiles,
            acceptedFiles,
            sampleMayBeCroppedMessage,
            sampleCroppedMessage,
        };
    },
});
